/**
 * The landing page's half of `/api/landing/*`.
 *
 * Every getter here resolves to *something*. A marketing page that renders a
 * spinner because the API is having a bad minute is worse than one showing the
 * bundled copy, so each call falls back to what the client already has and
 * reports whether the answer came from the server.
 */

import {
  LANDING_CONTENT, CURATED_TICKER, TICKER_TONES, normaliseLanguage
} from '@shared/landing-content.js';

/**
 * The API's base.
 *
 * Derived from the document rather than hardcoded, so the same bundle works
 * behind a reverse proxy that mounts the site under a path. The English
 * twin at /en/ is the same site one folder down, not a site of its own: left
 * as it was, it asked /en/api/..., which the web server answers with a page.
 */
const BASE = new URL('.', window.location.href).href.replace(/\/en\/$/, '/').replace(/\/+$/, '');

const TIMEOUT_MS = 6000;

async function getJson(path, { timeoutMs = TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(BASE + path, {
      signal: controller.signal,
      headers: { accept: 'application/json' }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * What the page assumes before the server has answered.
 *
 * The single place these guesses live. The first paint happens before
 * `/api/landing/config` returns, so *something* has to fill the nav's links —
 * and if the API never answers, this is what the page ships with rather than
 * rendering dead buttons. Exported so `main.js` does not keep a second copy
 * that can drift out of step with this one.
 */
export function fallbackConfig() {
  return {
    /* From the site root: on /en/ a relative address led to /en/portfolio/, which is nothing. */
    appUrl: new URL('/portfolio/', window.location.origin).href,
    appPath: '/portfolio/',
    discordInviteUrl: 'https://discord.gg/liquidityraiders',
    waitlistEnabled: true,
    discordAuthEnabled: false,
    live: false
  };
}

/**
 * Where the tracker and the Discord server live, plus which optional sections
 * the server wants drawn.
 */
export async function fetchConfig() {
  const body = await getJson('/api/landing/config');
  const fallback = fallbackConfig();
  if (!body) return fallback;

  return {
    appUrl: body.appUrl || fallback.appUrl,
    appPath: body.appPath || fallback.appPath,
    discordInviteUrl: body.discordInviteUrl || fallback.discordInviteUrl,
    waitlistEnabled: body.waitlistEnabled !== false,
    discordAuthEnabled: Boolean(body.discordAuthEnabled),
    live: true
  };
}

/**
 * The copy.
 *
 * Returned from the bundle immediately and refreshed from the server after, so
 * the first paint never waits on a round trip for words the client already has.
 */
export function contentSync(language) {
  return LANDING_CONTENT[normaliseLanguage(language)];
}

export async function fetchContent(language) {
  const body = await getJson(`/api/landing/content?lang=${encodeURIComponent(language)}`);
  if (!body?.content) return contentSync(language);
  /*
   * Merged over the bundle, not swapped for it. A server running an older build
   * answers with an older shape, and every `t.something.else` in the render path
   * would throw on the missing key — turning a stale deploy into a blank page.
   * Shallow is enough: the server either has a section or it does not.
   */
  return { ...contentSync(language), ...body.content };
}

/**
 * The server's headcount.
 *
 * A zero is normalised to null, not passed through: the server uses zero for
 * "Discord could not be reached and no fallback was configured", and the page
 * has to be able to tell that apart from a real reading so it can hide the
 * counter rather than announce an empty server.
 */
export async function fetchDiscord() {
  const body = await getJson('/api/landing/discord');
  if (!body) return { online: null, members: null, countMode: 'members', inviteUrl: null, live: false };

  const count = value => (Number.isFinite(value) && value > 0 ? value : null);
  return {
    online: count(body.online),
    members: count(body.members),
    countMode: body.countMode === 'online' ? 'online' : 'members',
    inviteUrl: body.inviteUrl || null,
    live: Boolean(body.live)
  };
}

export async function fetchStats(language) {
  const body = await getJson(`/api/landing/stats?lang=${encodeURIComponent(language)}`);
  if (!body?.ticker?.length) {
    return { ticker: CURATED_TICKER[normaliseLanguage(language)], tones: TICKER_TONES, live: false };
  }
  return { ticker: body.ticker, tones: body.tones || TICKER_TONES, live: Boolean(body.live) };
}

/**
 * Signs an address up.
 *
 * Unlike the getters, this one reports failure: the visitor pressed a button
 * and is owed an answer, so a network error surfaces as `outcome: 'error'`
 * rather than being smoothed over into a false "check your inbox".
 */
export async function submitWaitlist({ email, language, company }) {
  try {
    const response = await fetch(BASE + '/api/landing/waitlist', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, language, company, source: 'landing' })
    });

    if (response.status === 429) return { outcome: 'rate_limited' };
    if (response.status === 400) return { outcome: 'invalid' };
    if (!response.ok) return { outcome: 'error' };

    const body = await response.json().catch(() => ({}));
    return { outcome: body.outcome || 'created', delivered: body.delivered };
  } catch {
    return { outcome: 'error' };
  }
}
